import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FileText, FileSpreadsheet, FileType, Globe, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportCreateDialog } from "@/components/feature/media/report-create-dialog";
import { media } from "@/lib/api";
import { useActiveClient } from "@/hooks/use-active-client";

export const Route = createFileRoute("/_auth/media/reports")({
  component: MediaReports,
});

function MediaReports() {
  const navigate = useNavigate();
  const { activeClientId } = useActiveClient();
  const { data, isLoading } = useQuery({
    queryKey: ["media", "reports", activeClientId],
    queryFn: () => media.listReports({ limit: 100, client_id: activeClientId ?? undefined }),
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Reportes de transparencia</h2>
        <ReportCreateDialog />
      </div>

      {isLoading ? <Skeleton className="h-40" /> : (
        <div className="space-y-3">
          {(data ?? []).map(r => (
            <Card key={r.id} className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate({ to: "/media/reports/$reportId", params: { reportId: String(r.id) } })}
            >
              <CardContent className="py-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FormatIcon format={r.format} />
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {r.name}
                      {r.is_public && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Globe size={10} /> Público
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {r.kind} · {r.format.toUpperCase()}
                      {r.period_label && ` · ${r.period_label}`}
                      {r.client_id ? ` · cliente #${r.client_id}` : " · todos los clientes"}
                    </div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-ink-400" />
              </CardContent>
            </Card>
          ))}
          {!(data ?? []).length && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay reportes. Crea el primero con "Nuevo reporte".
            </div>
          )}
        </div>
      )}
    </>
  );
}

function FormatIcon({ format }: { format: string }) {
  if (format === "xlsx") return <FileSpreadsheet size={20} className="text-emerald-600 shrink-0" />;
  if (format === "pdf") return <FileText size={20} className="text-rose-600 shrink-0" />;
  if (format === "pptx") return <FileType size={20} className="text-orange-600 shrink-0" />;
  return <FileText size={20} className="text-ink-400 shrink-0" />;
}
